import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Check, Copy, Download, Music as MusicIcon, Plus, RotateCcw, X } from 'lucide-react';
import { useFlash } from '../context/ToastContext.jsx';
import ConfirmModal from '../components/ConfirmModal.jsx';
import DynamicField from '../components/DynamicField.jsx';
import InMatchCard from '../components/InMatchCard.jsx';
import Loader from '../components/Loader.jsx';
import ObsControlCard from '../components/ObsControlCard.jsx';
import OverlayPreview from '../components/OverlayPreview.jsx';
import Top8SnapshotCard from '../components/Top8SnapshotCard.jsx';
import TournamentCard from '../components/TournamentCard.jsx';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useStreamConfig } from '../hooks/useOverlayConfig.js';
import { downloadObsSceneCollection, IN_MATCH_SLUG, IN_MATCH_SWAPPED_SUFFIX, MUSIC, STINGERS, swappedOverlayUrl } from '../lib/obsExport.js';
import { obsControl } from '../lib/obsControl.js';
import { obsDirector } from '../lib/obsDirector.js';
import { obsStage } from '../lib/obsStage.js';
import { obsRemote } from '../lib/obsRemote.js';
import { downloadVmixPreset } from '../lib/vmixExport.js';
import {
  colSpanForType,
  getValueAtPath,
  setValueAtPath,
} from '../lib/variableSchema.js';

// title / titleAr: the tier's heading on the overlays while the console
// leaves it unnamed — the placeholders of the title fields.
const TIERS = [
  { id: 'diamond', label: 'Main Partner', title: 'Main Partners', titleAr: 'الشركاء الرئيسيون', accent: '#b3e5ff' },
  { id: 'gold',    label: 'Gold',    title: 'Gold Partners',   titleAr: '', accent: '#ffd54f' },
  { id: 'silver',  label: 'Silver',  title: 'Silver Partners', titleAr: '', accent: '#c0c4d6' },
];

// Packages whose overlay pages show the console's sponsor titles: the KEC
// screens, whose client has an organizer and partners rather than ranked
// tiers. Tekken 8's pages keep their fixed headings, so its console doesn't
// offer the fields.
const SPONSOR_TITLE_PACKAGES = new Set(['valorant', 'marvel-rivals']);

// Packages whose commentator page puts each commentator on camera: it has a
// sponsor band and can give two commentators one shared camera window, but
// no tournament logo and no role line, so the Commentators tab swaps that
// switch for these and leaves out the Role field.
const CAMERA_COMMENTATOR_PACKAGES = new Set(['valorant', 'marvel-rivals']);
// Of those, the package whose commentator page is a booth of two windows over
// the Up Next screen and the bracket (the OBS export's
// BOOTH_COMMENTATOR_PACKAGES); the other's windows are cameras.
const BOOTH_COMMENTATOR_PACKAGES = new Set(['valorant']);

const DEFAULT_STATE = {
  tournament: { org: '', name: '', hashtag: '', handle: '', logo: null },
  // characters / hudFit / autoStart / onStream: Tekken 8's In-Match card (InMatchCard).
  // bestOf / bestOfLate: the KEC In-Match cards' Best of fields (migration 0055).
  match: {
    day: '',
    upNext: '',
    characters: { left: '', right: '', setId: '', swapped: false },
    hudFit: { size: 100, x: 0, y: 0 },
    autoStart: false,
    onStream: { setId: '' },
    bestOf: '',
    bestOfLate: '',
  },
  startgg: { slug: '', stream: '' },
  sponsors: { diamond: [], gold: [], silver: [] },
  // Each tier's name on the overlays (SPONSOR_TITLE_PACKAGES only).
  sponsorTitles: {
    diamond: { en: '', ar: '' },
    gold:    { en: '', ar: '' },
    silver:  { en: '', ar: '' },
  },
  starting: { countdown: { endsAt: null } },
  brb: { countdown: { endsAt: null } },
  // Set by /api/packages/[slug]/top8 (snapshot endpoint) — not user-edited.
  // Read by the Top 8 OBS overlay and the admin preview card.
  top8: null,
  commentators: {
    left:  { name: '', handle: '', role: '' },
    right: { name: '', handle: '', role: '' },
  },
  // VALORANT Up Next: the players' Riot IDs, matched to start.gg names.
  valorant: { riotIds: '' },
  // Per-overlay visibility toggles — mirrors defaultState() in
  // functions/_lib/packages.js. Defaults TRUE; overlays hide only on an
  // explicit false.
  display: {
    starting:    { sponsors: true, logo: true },
    brb:         { sponsors: true, logo: true },
    thanks:      { sponsors: true, logo: true },
    camera:      { sponsors: true, logo: true },
    match:       { sponsors: true, logo: true, ranks: true },
    // top8.sponsors: unused since 2026-10-30 (the VALORANT Top 8 and champion
    // show no sponsors); kept so older saved states keep their shape.
    top8:        { logo: true, sponsors: true },
    commentator: { logo: true, sponsors: true, shared: false, switchAt: null },
    upnext:      { sponsors: true, ranks: true },
    bracket:     { sponsors: true },
  },
};

const AUTOSAVE_DELAY_MS = 1200;
// How far ahead a change of the commentator layout is set (server ms): the
// save lands within a second and the overlay polls every 2 s, so it is
// always there in time, and the OBS Control's move lands on the same instant.
const COMMENTATOR_SWITCH_LEAD_MS = 3000;

// All overlays poll on a fixed 5-second cadence. The constant is baked into
// the In-Match URL so old code paths that read `?refresh=` still work, but
// there's no UI to change it — every overlay should tick on the same beat
// so scores / nameplates / round-text update together on stream.
const OVERLAY_REFRESH_SEC = 5;

// Per-package, per-overlay URL builder.
//
// Token mode (Phase 2): overlay row carries an access_token from the
// user_overlays grant → URL is just /o/<access_token>. Server resolves
// the token to (user, overlay, package), reads slug/stream from
// overlay_configs.stream, and serves the per-user HTML the admin
// uploaded to R2.
//
// Legacy mode: no token (admin viewing an overlay they have implicit
// access to but no explicit grant for). Falls back to the shared
// template at /overlay/<slug>.html with ?key=&slug=&stream= so admin
// preview keeps working until they grant themselves access. Tekken 8 only:
// the legacy /api/overlay/* endpoints serve that package's state and the
// shared templates are Tekken-branded, so any other package needs a grant.
function buildOverlayUrl({ overlaySlug, overlayKey, accessToken, baseUrl, streamCfg, packageSlug }) {
  if (accessToken) {
    // ?key= ties the URL to the user's CURRENT overlay_key. Rotation
    // changes the key and invalidates every old OBS URL across all of
    // this user's overlays — that's the intended security gate.
    const keyParam = overlayKey ? `?key=${encodeURIComponent(overlayKey)}` : '';
    return `${baseUrl}/o/${accessToken}${keyParam}`;
  }
  if (packageSlug !== 'tekken-8') return null;

  const baseParams = new URLSearchParams();
  if (overlayKey) baseParams.set('key', overlayKey);
  if (streamCfg?.slug) baseParams.set('slug', streamCfg.slug);
  if (streamCfg?.stream) baseParams.set('stream', streamCfg.stream);
  const query = baseParams.toString() ? `?${baseParams.toString()}` : '';

  const staticDir = '/overlay';

  switch (overlaySlug) {
    case 'in-match':
      // Static HTML at /overlay/in_match.html — same delivery pattern as
      // the other four overlays. The HTML's embedded JS polls
      // /api/overlay/stream and renders nameplates client-side. Slug +
      // stream are required here because the legacy poll endpoint reads
      // them from the URL query.
      if (!streamCfg?.slug || !streamCfg?.stream) return null;
      return `${baseUrl}${staticDir}/in_match.html${query}&refresh=${OVERLAY_REFRESH_SEC}`;
    case 'starting-soon':
      return `${baseUrl}${staticDir}/starting_soon.html${query}`;
    case 'camera-feed':
      return `${baseUrl}${staticDir}/camera_feed.html${query}`;
    case 'be-right-back':
      return `${baseUrl}${staticDir}/be_right_back.html${query}`;
    case 'thank-you':
      return `${baseUrl}${staticDir}/thank_you.html${query}`;
    case 'top-8':
      return `${baseUrl}${staticDir}/top_8.html${query}`;
    case 'top-8-bracket':
      // Bracket auto-resolves its event from the stream (slug + stream in the
      // query, when set) or the Top 8 snapshot — no extra params needed here.
      return `${baseUrl}${staticDir}/top_8_bracket.html${query}`;
    case 'winner-screen':
      return `${baseUrl}${staticDir}/winner_screen.html${query}`;
    default:
      return null;
  }
}

function CopyableUrl({ value, accent }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  function execCommandCopy() {
    const input = inputRef.current;
    if (!input) throw new Error('input ref missing');
    input.focus();
    input.select();
    input.setSelectionRange(0, value.length);
    const ok = document.execCommand('copy');
    input.blur();
    if (!ok) throw new Error('execCommand returned false');
  }

  async function copy() {
    if (!value) return;
    setError(null);
    setCopied(false);
    let succeeded = false;
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      try { await navigator.clipboard.writeText(value); succeeded = true; }
      catch { /* fall through */ }
    }
    if (!succeeded) {
      try { execCommandCopy(); succeeded = true; }
      catch (e) {
        setError(e.message || 'Copy failed');
        setTimeout(() => setError(null), 3000);
        return;
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const disabled = !value;
  const btnBg = copied ? 'var(--success)' : (error ? 'var(--danger)' : 'transparent');
  const btnFg = copied || error ? '#000' : (accent || 'var(--neon-blue)');

  return (
    <div className="input-group input-group-sm">
      <input
        ref={inputRef}
        type="text"
        className="form-control form-control-sm"
        value={value || ''}
        readOnly
        placeholder={disabled ? 'Set slug & stream first' : ''}
      />
      <button
        className="btn btn-sm d-inline-flex align-items-center justify-content-center"
        type="button"
        onClick={copy}
        disabled={disabled}
        aria-label={copied ? 'Copied' : error ? 'Copy failed' : 'Copy URL'}
        title={copied ? 'Copied!' : error ? `Copy failed: ${error}` : 'Copy URL'}
        style={{
          background: btnBg, color: btnFg,
          // Keep a 1px transparent border so height aligns with the
          // sibling form-control input (which carries its own 1px
          // border). Visible bg fill on copied/failed still reads as
          // a button without an outline.
          borderColor: 'transparent',
          minWidth: 36, padding: '0 10px',
        }}
      >
        {copied
          ? <Check size={16} aria-hidden="true" />
          : error
            ? <X size={16} aria-hidden="true" />
            : <Copy size={16} aria-hidden="true" />}
      </button>
    </div>
  );
}

// A settings card for one overlay in the Overlays tab: header (name + an
// optional badge) then its controls. OBS URLs live in the Links tab now.
function OverlayCard({ name, badge, children }) {
  return (
    <div className="card card-fui overlay-card">
      <div className="card-header d-flex align-items-center justify-content-between gap-2">
        <h6 className="mb-0">
          <img src="/images/section-icon.png" className="pp-icon" alt="" aria-hidden="true" /> {name}
        </h6>
        {badge}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

// One-liners for overlays that have no per-overlay config (their data comes
// from the start.gg link + shared settings). Shown under the URL so the card
// isn't just a bare link.
const OVERLAY_BLURB = {
  'in-match': 'Live nameplates from the current set on your stream — no per-overlay settings.',
  'camera-feed': 'Player + gameplay cam frames and the sponsor bar. Sponsors are managed in the Sponsors section below.',
  'thank-you': 'Post-event thank-you card — uses your tournament branding.',
  'top-8-bracket': 'Live Top 8 bracket, auto-detected from the event on your stream. Loads once — refresh the OBS source to update it.',
};

// onTitle is set only where the package's pages show sponsor titles; the
// column's heading then follows the name typed for it.
function TierColumn({ tier, sponsors, title, onTitle, onUpload, onDelete, busy, loading }) {
  const fileInput = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  async function uploadFiles(files) {
    // Single onUpload call with the whole array — the parent batches the
    // re-fetch + busy/loader state so the user sees one continuous spinner
    // instead of one flicker per file.
    if (files.length) await onUpload(tier.id, files);
  }

  return (
    <div
      className="tier-card"
      style={{ '--tier-accent': tier.accent }}
    >
      <img
        className="tier-card__watermark"
        src={`/images/tiers/${tier.id}.png`}
        alt=""
        aria-hidden="true"
      />
      <h3 className="tier-card__title">
        {(onTitle && ((title?.en ?? '').trim() || (title?.ar ?? '').trim())) || tier.label}
      </h3>
      <p className="tier-card__count">
        {sponsors.length} {sponsors.length === 1 ? 'sponsor' : 'sponsors'}
      </p>
      {onTitle && (
        <div className="tier-card__titles">
          <label className="form-label small mb-1" htmlFor={`sponsor-title-${tier.id}`}>
            Title on screen
          </label>
          <input
            id={`sponsor-title-${tier.id}`}
            className="form-control mb-2"
            value={title?.en ?? ''}
            maxLength={40}
            placeholder={tier.title}
            onChange={(e) => onTitle(tier.id, 'en', e.target.value)}
          />
          <input
            className="form-control"
            dir="rtl"
            lang="ar"
            aria-label="Title on screen, in Arabic"
            value={title?.ar ?? ''}
            maxLength={40}
            placeholder={tier.titleAr || 'Arabic'}
            onChange={(e) => onTitle(tier.id, 'ar', e.target.value)}
          />
        </div>
      )}
      <div
        className={`sponsor-dropzone ${dragOver ? 'is-active' : ''}`}
          role="button"
          tabIndex={busy ? -1 : 0}
          aria-disabled={busy}
          onClick={() => { if (!busy) fileInput.current?.click(); }}
          onKeyDown={(e) => {
            if (busy) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInput.current?.click();
            }
          }}
          onDragOver={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); if (!busy) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (busy) return;
            const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/'));
            if (files.length) uploadFiles(files);
          }}
        >
          <div>Drop logos here, or click to browse</div>
          <span className="sponsor-dropzone-hint">PNG, JPG, GIF, WEBP, SVG · multiple files OK</span>
          <input
            ref={fileInput}
            type="file"
            className="d-none"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            multiple
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              await uploadFiles(files);
              e.target.value = '';
            }}
          />
        </div>
        {sponsors.length > 0 && (
          <ul className="sponsor-list">
            {sponsors.map((s) => (
              <li key={s.key}>
                <img className="sponsor-thumb" src={s.url} alt={s.name} />
                <span className="sponsor-name" title={s.name}>{s.name}</span>
                <button
                  type="button"
                  className="sponsor-remove"
                  onClick={() => onDelete(tier.id, s.key)}
                  title={`Remove ${s.name}`}
                  aria-label={`Remove ${s.name}`}
                  disabled={busy}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      {loading && (
        <div
          className="sponsor-card-loading"
          aria-live="polite"
          aria-busy="true"
        >
          <span
            className="loader"
            style={{ width: 32, height: 32, borderWidth: 3 }}
            role="status"
            aria-label="Working"
          />
        </div>
      )}
    </div>
  );
}

function SaveStatus({ saving, dirty }) {
  let label = 'All changes saved';
  let color = 'var(--success)';
  if (saving) { label = 'Saving…'; color = 'var(--warning)'; }
  else if (dirty) { label = 'Unsaved changes'; color = 'var(--text-secondary)'; }
  return (
    <span className="pp-save" style={{ color }}>
      <span className="pp-save__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export default function PackageProduction() {
  const { slug: packageSlug } = useParams();
  const { overlayKey, user } = useAuth();
  const [streamCfg, setStreamCfg, streamMeta] = useStreamConfig();

  // The package + the user's accessible overlays inside it. null = still
  // loading, undefined = no access / doesn't exist.
  const [pkg, setPkg] = useState(null);
  const [accessChecked, setAccessChecked] = useState(false);

  const [state, setState] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  // Which tier is currently uploading or deleting. Drives a per-card
  // loader overlay so the user gets clear feedback without waiting on
  // a global spinner. `busy` is still set for the duration so other
  // tiers' inputs stay disabled (avoids racey concurrent re-fetches).
  const [busyTier, setBusyTier] = useState(null);
  const setFlash = useFlash();
  const [resetOpen, setResetOpen] = useState(false);
  // ?tab=obs opens on the OBS Control tab (the deck page's way back).
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'connection');

  const [slug, setSlug] = useState('');
  const [stream, setStream] = useState('');
  // Licensing: the on-air run's tournament slug, fed up by TournamentCard.
  // Under enforcement it replaces the editable slug everywhere the console
  // needs one (the Top 8 snapshot's event dropdown) — the START GG card is
  // display-only by design (brief §4).
  const [onAirSlug, setOnAirSlug] = useState(null);
  const billingEnforced = user?.billingEnforced === true;
  // Admin-set per-user flag: hides the tournament/license history on this
  // console (fold + standalone card) without touching the underlying rows.
  const historyEnabled = user?.tournamentHistoryEnabled !== false;

  // start.gg slug validation — 'idle' | 'checking' | 'ok' | 'bad'.
  // "Connected" only shows after the tournament-events lookup succeeds for
  // the current slug; validation fires on slug blur (and once after the
  // saved slug hydrates). A seq counter drops stale responses when the
  // user re-blurs before the previous lookup lands.
  //
  // The same lookup's response feeds the Top 8 card's event dropdown —
  // one /api/tournament/events fetch serves both, instead of the card
  // re-fetching on every Overlays-tab visit (each one a real start.gg
  // request once the server's 4 s cache lapses).
  const [connState, setConnState] = useState('idle');
  const [events, setEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const connSeq = useRef(0);
  const lastEventsSlugRef = useRef(null);

  const loadEvents = async (rawSlug, { force = false } = {}) => {
    const s = String(rawSlug ?? '').trim();
    // A successful lookup for this exact slug is already in hand — blurring
    // the untouched field again shouldn't burn another start.gg request.
    // Failures are NOT latched, so re-blur retries; the card's refresh
    // button passes force to pick up newly added events mid-tournament.
    if (!force && s === lastEventsSlugRef.current && connState === 'ok') return;
    const seq = ++connSeq.current;
    lastEventsSlugRef.current = s;
    if (!s) {
      setConnState('idle');
      setEvents(null);
      setEventsError(null);
      return;
    }
    setConnState('checking');
    setEventsLoading(true);
    setEventsError(null);
    try {
      // Any 2xx means start.gg resolved the tournament (an unknown slug
      // throws StartGGError server-side → non-2xx → catch).
      const data = await api.tournamentEvents(s, packageSlug);
      if (connSeq.current !== seq) return;
      setEvents(data?.events ?? []);
      setConnState('ok');
    } catch (e) {
      if (connSeq.current !== seq) return;
      setEvents([]);
      setEventsError(e?.message ?? String(e));
      setConnState('bad');
    } finally {
      if (connSeq.current === seq) setEventsLoading(false);
    }
  };

  const initialLoad = useRef(true);
  const lastSavedRef = useRef(null);
  const streamHydrated = useRef(false);
  const streamInitialWrite = useRef(true);
  // Set by discrete-action onChange callbacks (countdown Start / Clear)
  // so the next autosave skips the debounce window. Resets after the save
  // is scheduled. Continuous-typing fields don't touch it, so they keep
  // the standard debounce.
  const flushImmediateRef = useRef(false);

  // serverNow - localNow at config-load time. Forwarded to interactive
  // fields like the countdown so a skewed local clock doesn't inflate
  // stored values (the OBS overlay reads them against server time —
  // a +3min local drift would otherwise make a "10 min" countdown
  // render as 13 min).
  const serverOffsetRef = useRef(0);

  // The commentator layout (Camera only, or the two windows over Up Next and
  // the bracket) changes at a set time, COMMENTATOR_SWITCH_LEAD_MS ahead on
  // the server's clock, written with the switch and saved at once: the
  // overlay slides at that instant and the OBS Control moves OBS's camera,
  // Up Next and Bracket with it (obsStage.js), so the holes and the sources
  // travel together.
  const setCommentatorLayout = (shared) => {
    flushImmediateRef.current = true;
    setState((prev) => setValueAtPath(
      setValueAtPath(prev, 'display.commentator.shared', shared === true),
      'display.commentator.switchAt',
      Date.now() + serverOffsetRef.current + COMMENTATOR_SWITCH_LEAD_MS,
    ));
  };

  // The OBS remote lends this switch to a remote's Commentator windows
  // buttons (src/lib/obsRemote.js): this console writes the state, so its
  // autosave never puts an older value back.
  useEffect(() => {
    obsRemote.setLayoutHandler(setCommentatorLayout);
    return () => obsRemote.setLayoutHandler(null);
  });

  // ── access check + package metadata ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setPkg(null);
    setAccessChecked(false);
    api.myPackages()
      .then((data) => {
        if (cancelled) return;
        const found = (data?.packages ?? []).find((p) => p.slug === packageSlug);
        setPkg(found ?? undefined);
        setAccessChecked(true);
      })
      .catch(() => {
        if (!cancelled) {
          setPkg(undefined);
          setAccessChecked(true);
        }
      });
    return () => { cancelled = true; };
  }, [packageSlug]);

  // ── OBS control + auto winner-screen director wiring ────────────────────
  // Page-level on purpose (the ObsControlCard repeats it on its own mount):
  // the director must resolve scene names and watch the stream even when the
  // operator never opens the OBS Control tab this session. connect() is a
  // no-op unless the operator enabled OBS control on this machine, and the
  // director additionally requires its own per-machine arm toggle — so for
  // everyone else this effect only records config.
  useEffect(() => {
    if (!pkg) return;
    const granted = pkg.overlays ?? [];
    obsControl.activateSceneMap(
      packageSlug,
      Object.fromEntries(granted.map((o) => [o.slug, o.name])),
    );
    obsControl.connect();
    // The OBS remote (src/lib/obsRemote.js): with OBS control on, this tab
    // relays cuts from this account's other consoles; page-level too.
    obsRemote.configure({ packageSlug });
    const inMatch = granted.find((o) => o.slug === IN_MATCH_SLUG);
    obsDirector.configure({
      packageSlug,
      streamPath:
        inMatch?.accessToken && overlayKey
          ? `/api/o/${inMatch.accessToken}/stream?key=${encodeURIComponent(overlayKey)}`
          : null,
      hasWinnerOverlay: granted.some((o) => o.slug === 'winner-screen'),
      // Fold successful stamps into the local form: the console autosaves a
      // FULL config document, so a stale local winner would overwrite the
      // director's write on the next save. Redundantly PUTting the same
      // stamped values back is harmless; reverting them on air is not.
      onStamp: (winner) =>
        setState((prev) => ({ ...prev, winner: { ...prev.winner, ...winner } })),
    });
  }, [pkg, packageSlug, overlayKey]);

  // The OBS Control tab's automation (src/lib/obsStage.js) follows this
  // console's commentator layout and its two countdowns, page-level like the
  // director so it runs with the tab closed. Not before the saved state has
  // loaded: the defaults would put OBS's cameras in a layout the page isn't
  // drawing.
  const commentatorShared = state?.display?.commentator?.shared === true;
  const commentatorSwitchAt = state?.display?.commentator?.switchAt ?? null;
  const startingEndsAt = state?.starting?.countdown?.endsAt ?? null;
  const brbEndsAt = state?.brb?.countdown?.endsAt ?? null;
  const commentators = state?.commentators;
  useEffect(() => {
    if (!pkg || loading) return;
    obsStage.configure({
      packageSlug,
      overlays: (pkg.overlays ?? []).map((o) => ({ slug: o.slug, name: o.name })),
      commentators,
      shared: commentatorShared,
      switchAt: commentatorSwitchAt,
      startingEndsAt,
      brbEndsAt,
      serverOffset: serverOffsetRef.current,
    });
  }, [pkg, loading, packageSlug, commentators, commentatorShared, commentatorSwitchAt, startingEndsAt, brbEndsAt]);

  // Enforced users never type a slug — when the on-air tournament resolves,
  // load its events so the Top 8 snapshot dropdown works unchanged.
  useEffect(() => {
    if (billingEnforced && onAirSlug) loadEvents(onAirSlug);
  }, [billingEnforced, onAirSlug]);

  // ── load existing package state once we know the user has access ───────
  useEffect(() => {
    if (!pkg) return;
    let cancelled = false;
    setLoading(true);
    initialLoad.current = true;
    api.packageGetConfig(packageSlug)
      .then((cfg) => {
        if (cancelled) return;
        // Capture server-time offset before stripping `now` from the merged
        // state shape (we don't want `now` autosaving back into state).
        if (typeof cfg?.now === 'number') {
          serverOffsetRef.current = cfg.now - Date.now();
        }
        const { now, ...rest } = cfg ?? {};
        const merged = { ...DEFAULT_STATE, ...rest };
        setState(merged);
        lastSavedRef.current = JSON.stringify(merged);
      })
      .catch((e) => { if (!cancelled) setFlash({ kind: 'danger', msg: e.message }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [pkg, packageSlug]);

  // ── hydrate slug/stream from the user's overlay_configs row ───────────
  // (refresh isn't user-editable any more — all overlays tick on a fixed
  // 5 s cadence; see OVERLAY_REFRESH_SEC.)
  useEffect(() => {
    if (!streamMeta.loading && !streamHydrated.current) {
      setSlug(streamCfg?.slug ?? '');
      setStream(streamCfg?.stream ?? '');
      streamHydrated.current = true;
      // Validate the saved slug once so a returning user sees the real
      // connection state without having to touch the field.
      loadEvents(streamCfg?.slug ?? '');
    }
  }, [streamMeta.loading, streamCfg]);

  // The stream's background music (the KEC packages): the track the admin
  // picked in Package Art, for the Music button beside the OBS export, which
  // downloads it under the name the export's Background Music source plays.
  const music = MUSIC[packageSlug] ?? null;
  const [musicTrack, setMusicTrack] = useState(null);
  useEffect(() => {
    if (!music) { setMusicTrack(null); return undefined; }
    let cancelled = false;
    fetch(`/api/art/music/${packageSlug}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setMusicTrack(d?.track ?? null); })
      .catch(() => { if (!cancelled) setMusicTrack(null); });
    return () => { cancelled = true; };
  }, [packageSlug, music]);

  // ── autosave package state (debounced) ─────────────────────────────────
  useEffect(() => {
    if (loading || !pkg) return;
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    const current = JSON.stringify(state);
    if (current === lastSavedRef.current) {
      setDirty(false);
      return;
    }
    setDirty(true);
    // Discrete actions (countdown Start / Clear) flag the upcoming write
    // as immediate so OBS overlays pick it up on their next 2 s poll
    // instead of waiting an extra 1.2 s for the debounce.
    const delay = flushImmediateRef.current ? 0 : AUTOSAVE_DELAY_MS;
    flushImmediateRef.current = false;
    const t = setTimeout(async () => {
      setSaving(true);
      try {
        await api.packagePutConfig(packageSlug, state);
        lastSavedRef.current = current;
        setDirty(false);
      } catch (e) {
        showFlash('danger', e.message);
      } finally {
        setSaving(false);
      }
    }, delay);
    return () => clearTimeout(t);
  }, [state, loading, pkg, packageSlug]);

  // ── debounced write of slug/stream ─────────────────────────────────────
  useEffect(() => {
    if (!streamHydrated.current) return;
    if (streamInitialWrite.current) {
      streamInitialWrite.current = false;
      return;
    }
    const t = setTimeout(() => {
      const trimmedSlug = slug.trim();
      const trimmedStream = stream.trim();
      if (!trimmedSlug && !trimmedStream) {
        setStreamCfg(null).catch(() => {});
      } else {
        // refresh is fixed at OVERLAY_REFRESH_SEC; keep it on the row for
        // any code path (or future config) that still reads it.
        setStreamCfg({
          slug: trimmedSlug,
          stream: trimmedStream,
          refresh: OVERLAY_REFRESH_SEC,
        }).catch(() => {});
      }
    }, 600);
    return () => clearTimeout(t);
  }, [slug, stream, setStreamCfg]);

  function showFlash(kind, msg) {
    setFlash({ kind, msg });
  }

  // Refetch the server-side config and replace local state with it.
  // Used by side-effect actions that mutate package_state out-of-band
  // (snapshot endpoint, clear, etc.) — bumps lastSavedRef so the existing
  // autosave loop doesn't fire a redundant PUT of the same blob.
  async function refetchConfig() {
    const fresh = await api.packageGetConfig(packageSlug);
    if (typeof fresh?.now === 'number') {
      serverOffsetRef.current = fresh.now - Date.now();
    }
    const { now: _now, ...rest } = fresh ?? {};
    const merged = { ...DEFAULT_STATE, ...rest };
    setState(merged);
    lastSavedRef.current = JSON.stringify(merged);
  }

  const setSponsorTitle = (tier, lang, value) =>
    setState((prev) => setValueAtPath(prev, `sponsorTitles.${tier}.${lang}`, value));

  // Batch upload: handles N files in one busy transition with a single
  // re-fetch at the end. Avoids per-file spinner flicker when the user
  // drops multiple files at once.
  async function handleUpload(tier, files) {
    if (!files?.length) return;
    setBusy(true);
    setBusyTier(tier);
    let lastEntry = null;
    let uploaded = 0;
    try {
      for (const f of files) {
        lastEntry = await api.packageUploadLogo(packageSlug, tier, f);
        uploaded++;
      }
      const fresh = await api.packageGetConfig(packageSlug);
      const merged = { ...DEFAULT_STATE, ...fresh };
      setState(merged);
      lastSavedRef.current = JSON.stringify(merged);
      showFlash(
        'success',
        uploaded === 1
          ? `Uploaded ${lastEntry?.name ?? '1 logo'}`
          : `Uploaded ${uploaded} logos`,
      );
    } catch (e) {
      showFlash('danger', `Upload failed: ${e.message}`);
    } finally {
      setBusy(false);
      setBusyTier(null);
    }
  }

  async function handleDelete(tier, key) {
    setBusy(true);
    setBusyTier(tier);
    try {
      await api.packageDeleteLogo(packageSlug, tier, key);
      const fresh = await api.packageGetConfig(packageSlug);
      const merged = { ...DEFAULT_STATE, ...fresh };
      setState(merged);
      lastSavedRef.current = JSON.stringify(merged);
    } catch (e) {
      showFlash('danger', `Delete failed: ${e.message}`);
    } finally {
      setBusy(false);
      setBusyTier(null);
    }
  }

  async function confirmResetAll() {
    setBusy(true);
    try {
      for (const tier of ['diamond', 'gold', 'silver']) {
        for (const s of state.sponsors[tier] || []) {
          try { await api.packageDeleteLogo(packageSlug, tier, s.key); }
          catch { /* keep going */ }
        }
      }
      const cleared = JSON.parse(JSON.stringify(DEFAULT_STATE));
      await api.packagePutConfig(packageSlug, cleared);
      setState(cleared);
      lastSavedRef.current = JSON.stringify(cleared);
      try { await setStreamCfg(null); } catch { /* keep going */ }
      setSlug('');
      setStream('');
      showFlash('success', 'Everything has been reset.');
    } catch (e) {
      showFlash('danger', `Reset failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  // ── access / loading screens ────────────────────────────────────────────
  if (!accessChecked) {
    return <Loader label="Loading package…" />;
  }
  if (!pkg) {
    return (
      <div className="row justify-content-center mt-5">
        <div className="col-md-6 text-center">
          <h5 className="text-muted mb-2">No access</h5>
          <p className="text-muted mb-3">
            You don't have any overlay grants in this package, or the package
            doesn't exist.
          </p>
          <Link to="/" className="btn btn-outline-primary btn-sm">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  if (loading) {
    return <Loader label="Loading config…" />;
  }

  const baseUrl = window.location.origin;

  // One built URL per granted overlay — powers the Links tab and the export.
  const overlayLinks = pkg.overlays.map((o) => ({
    overlay: o,
    url: buildOverlayUrl({
      overlaySlug: o.slug,
      overlayKey,
      accessToken: o.accessToken,
      baseUrl,
      streamCfg: { slug, stream },
      packageSlug: pkg.slug,
    }),
  }));
  const exportable = overlayLinks.filter(({ url }) => !!url);
  const missingUrl = overlayLinks.length - exportable.length;
  const overlayPayload = exportable.map(({ overlay, url }) => ({
    name: overlay.name,
    slug: overlay.slug,
    url,
  }));
  // Without a grant only Tekken 8 has a URL to fall back on (buildOverlayUrl).
  const readyNote = missingUrl > 0
    ? ` (${exportable.length} ready; ${missingUrl} ${pkg.slug === 'tekken-8' ? 'need slug & stream first' : 'need a grant first'})`
    : '';
  const stinger = STINGERS[packageSlug] ?? null;
  const obsHelp = `Downloads an OBS scene collection${readyNote}. Import via Scene Collection → Import.` +
    (stinger ? ` Its transition is the ${stinger.name}: save the stinger (the button beside this) as ${stinger.path.replace(/\//g, '\\')} first, or point OBS to it when it asks.` : '') +
    (music ? ` Its ${music.name} source plays ${music.path.replace(/\//g, '\\')}: save the track (the Music button) there.` : '');
  const stingerHelp = stinger
    ? `The ${stinger.name} video the OBS scenes use as their transition. Save it as ${stinger.path.replace(/\//g, '\\')}, or anywhere and point OBS to it when it asks.`
    : '';
  const musicHelp = music && musicTrack
    ? `${musicTrack.label}: the track the OBS scenes play as ${music.name} on every scene but In-Match (picked in Package Art → All screens → Music). Save it as ${music.path.replace(/\//g, '\\')}, or anywhere and point OBS to it when it asks.`
    : '';
  const vmixHelp = `Downloads a vMix preset${readyNote}. Open via File → Open.`;

  // ── tabs ──────────────────────────────────────────────────────────────────
  const overlays = pkg.overlays ?? [];
  const hasCommentator = overlays.some((o) => o.slug === 'commentator');
  const hasCameras = CAMERA_COMMENTATOR_PACKAGES.has(packageSlug);
  const boothCameras = BOOTH_COMMENTATOR_PACKAGES.has(packageSlug);
  // Overlays tab = every overlay except the commentator (its own tab), split
  // into ones with controls vs. link-only "live" overlays.
  const overlayTabItems = overlays.filter((o) => o.slug !== 'commentator');
  // Tekken 8's In-Match gets its own card for accounts with the character
  // picker or Start match (admin flags); for everyone else it stays link-only.
  const showPicker = user?.portraitMasksEnabled === true;
  const canStart = user?.startMatchEnabled === true;
  const hasInMatchCard = (o) =>
    o.slug === IN_MATCH_SLUG && packageSlug === 'tekken-8' && (showPicker || canStart);
  const configurableOverlays = overlayTabItems.filter(
    (o) => o.slug === 'top-8' || hasInMatchCard(o) || (o.variables?.length ?? 0) > 0,
  );
  const liveOnlyOverlays = overlayTabItems.filter(
    (o) => o.slug !== 'top-8' && !hasInMatchCard(o) && (o.variables?.length ?? 0) === 0,
  );
  const linksList = exportable; // overlays with a resolvable URL
  // badge counts rendered chips — the in-match ?flip twin adds one
  const linkCount =
    linksList.length + linksList.filter(({ overlay }) => overlay.slug === IN_MATCH_SLUG).length;
  const connLabel =
    connState === 'ok' ? 'Connected'
      : connState === 'checking' ? 'Checking…'
        : 'Not connected';

  const tabs = [
    { id: 'connection', label: 'Connection' },
    { id: 'overlays', label: 'Overlays' },
    ...(hasCommentator ? [{ id: 'commentators', label: 'Commentators' }] : []),
    ...(user?.sponsorsEnabled ? [{ id: 'sponsors', label: 'Sponsors' }] : []),
    { id: 'links', label: 'Links', badge: linkCount },
    // Same gate as the "Export to OBS" button: obsExportEnabled means "this
    // user runs OBS" — vMix-only users never see either surface. A dedicated
    // flag (migration 0020/0021 pattern) is the follow-up if scene control
    // ever becomes separately billable.
    ...(user?.obsExportEnabled !== false ? [{ id: 'obs', label: 'OBS Control' }] : []),
  ];
  const currentTab = tabs.some((t) => t.id === activeTab) ? activeTab : 'connection';

  return (
    <div className="overlay-console">
      <div
        className="sticky-top"
        style={{
          background: 'var(--bs-body-bg)',
          marginBottom: 18,
          zIndex: 100,
        }}
      >
        {/* Full-bleed console header strip (reference design): package key
            art under a dark scrim, magenta bloom, meta sub-line, giant
            watermark title. Art is package-specific, so the image stack
            stays inline (banner png, package jpg fallback). */}
        <div
          className="production-page-header mb-3"
          style={{
            backgroundImage:
              `linear-gradient(90deg, rgba(10,11,19,0.92) 0%, rgba(10,11,19,0.6) 55%, rgba(10,11,19,0.82) 100%),`
              + ` url(/api/packages/${pkg.slug}/art/banner),`
              + ` url(/api/packages/${pkg.slug}/art/card)`,
            backgroundSize: 'cover, cover, cover',
            backgroundPosition: 'center',
          }}
        >
          <span className="production-page-header__watermark" aria-hidden="true">
            {pkg.name}
          </span>
          <div className="production-page-header__inner">
            <div>
              <Link to="/" className="pp-back">← Dashboard</Link>
              <h3 className="mb-0 package-setup-title">{pkg.name} Setup</h3>
              <div className="production-page-header__meta">
                {overlays.length} overlay{overlays.length === 1 ? '' : 's'} · polling
                start.gg every {OVERLAY_REFRESH_SEC}s
              </div>
            </div>
            <SaveStatus saving={saving} dirty={dirty} />
          </div>
        </div>

        <nav className="prod-tabs" role="tablist" aria-label="Production sections">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={currentTab === t.id}
              className={`prod-tab ${currentTab === t.id ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
              {t.badge != null && <span className="prod-tab-badge">{t.badge}</span>}
            </button>
          ))}
          <span className="prod-tabs__exports">
            {user?.obsExportEnabled !== false && (
              <button
                type="button"
                className="prod-tabs__export"
                disabled={exportable.length === 0}
                title={obsHelp}
                onClick={() =>
                  downloadObsSceneCollection({
                    packageName: pkg.name,
                    packageSlug: pkg.slug,
                    overlays: overlayPayload,
                  })
                }
              >
                <img src="/images/OBS_Studio_logo.png" alt="" aria-hidden="true" />
                OBS Scenes
                <Download size={13} aria-hidden="true" />
              </button>
            )}
            {user?.obsExportEnabled !== false && stinger && (
              <a className="prod-tabs__export" href={stinger.file} download={stinger.saveAs} title={stingerHelp}>
                <img src="/images/OBS_Studio_logo.png" alt="" aria-hidden="true" />
                Stinger
                <Download size={13} aria-hidden="true" />
              </a>
            )}
            {user?.obsExportEnabled !== false && music && musicTrack && (
              <a className="prod-tabs__export" href={musicTrack.file} download={music.saveAs} title={musicHelp}>
                <MusicIcon size={14} aria-hidden="true" />
                Music
                <Download size={13} aria-hidden="true" />
              </a>
            )}
            {user?.vmixExportEnabled !== false && (
              <button
                type="button"
                className="prod-tabs__export"
                disabled={exportable.length === 0}
                title={vmixHelp}
                onClick={() => {
                  downloadVmixPreset({ packageSlug: pkg.slug, overlays: overlayPayload })
                    .catch((err) => setFlash({ kind: 'error', msg: `vMix export failed: ${err.message}` }));
                }}
              >
                <img src="/images/vMix_logo.svg" alt="" aria-hidden="true" />
                vMix Preset
                <Download size={13} aria-hidden="true" />
              </button>
            )}
          </span>
        </nav>
      </div>


      {/* ── Connection tab ── slug + stream feed every overlay's live data.
          Billing-enforced users get the Tournament card instead: routing
          comes from activations, so there is nothing to type here. */}
      {currentTab === 'connection' && billingEnforced && (
        <TournamentCard
          packageSlug={packageSlug}
          onFlash={showFlash}
          onOnAirSlug={setOnAirSlug}
          showHistory={historyEnabled}
        />
      )}
      {currentTab === 'connection' && !billingEnforced && (
        <div className="card card-fui mb-4">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h6 className="mb-0 d-inline-flex align-items-center">
              <img
                src="/images/overlays/startgg-logo.png"
                alt=""
                aria-hidden="true"
                style={{ height: 20, width: 'auto', marginRight: 8 }}
              />
              start.gg connection
            </h6>
            <span
              className={`conn-badge ${connState === 'ok' ? 'is-on' : ''}${connState === 'checking' ? ' is-checking' : ''}`}
              role="status"
            >
              <span className="conn-dot" />
              {connLabel}
            </span>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Tournament slug</label>
                <input
                  type="text"
                  className="form-control"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  onBlur={(e) => loadEvents(e.target.value)}
                  placeholder="e.g. tekken-cup-2026"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Stream username</label>
                <input
                  type="text"
                  className="form-control"
                  value={stream}
                  onChange={(e) => setStream(e.target.value)}
                  placeholder="e.g. nonivoid_"
                />
              </div>
            </div>
            <p className="form-text mt-3 mb-0">
              All overlays poll start.gg every {OVERLAY_REFRESH_SEC}s. Once connected,
              names and scores sync automatically.
            </p>
          </div>
        </div>
      )}
      {/* Unenforced clients with backfilled history still see their past
          tournaments + archived downloads — the card renders only when the
          billing read returns something to show, so everyone else's console
          stays pixel-identical. Gated at the mount so a hidden history
          skips the billing fetch entirely. */}
      {currentTab === 'connection' && !billingEnforced && historyEnabled && (
        <TournamentCard packageSlug={packageSlug} onFlash={showFlash} historyOnly />
      )}

      {/* ── Overlays tab ── each overlay's controls (URLs live in Links). */}
      {currentTab === 'overlays' && (
        <>
          <div className="overlay-masonry mb-3">
            {configurableOverlays.map((overlay) => {
              // Top 8: bespoke snapshot flow.
              if (overlay.slug === 'top-8') {
                return (
                  <div className="overlay-masonry__item" key={overlay.id}>
                    {/* tournamentSlug comes from the Connection tab's slug
                        (overlay_configs.stream), NOT state.startgg.slug. */}
                    <Top8SnapshotCard
                      packageSlug={packageSlug}
                      tournamentSlug={billingEnforced ? (onAirSlug ?? '') : slug}
                      top8={state.top8 ?? null}
                      events={events}
                      eventsLoading={eventsLoading}
                      eventsError={eventsError}
                      onRefreshEvents={() => loadEvents(billingEnforced ? (onAirSlug ?? '') : slug, { force: true })}
                      onRefresh={refetchConfig}
                      onFlash={showFlash}
                      showLogo={getValueAtPath(state, 'display.top8.logo') !== false}
                      onToggleLogo={(next) =>
                        setState((prev) => setValueAtPath(prev, 'display.top8.logo', next))}
                      // VALORANT: a pick for an entry's agent, kept in the
                      // snapshot itself (state.top8.entries[i].agent, source
                      // 'pick'; the automatic one kept as autoAgent so the
                      // pick can be undone), which the autosave writes and a
                      // re-snapshot keeps for the same entrant
                      // (_lib/valorantTop8.js). Saved at once: the export
                      // reads the stored state.
                      onAgentPick={(index, agent) => {
                        flushImmediateRef.current = true;
                        setState((prev) => {
                          const entries = (prev.top8?.entries ?? []).map((e, i) => {
                            if (i !== index) return e;
                            if (agent) {
                              return {
                                ...e,
                                autoAgent: 'autoAgent' in e ? e.autoAgent : (e.agent ?? null),
                                agent: { ...agent, source: 'pick' },
                              };
                            }
                            const { autoAgent, ...rest } = e;
                            return { ...rest, agent: autoAgent ?? null };
                          });
                          return { ...prev, top8: { ...prev.top8, entries } };
                        });
                      }}
                    />
                  </div>
                );
              }
              if (hasInMatchCard(overlay)) {
                return (
                  <div className="overlay-masonry__item" key={overlay.id}>
                    <InMatchCard
                      name={overlay.name}
                      packageSlug={packageSlug}
                      tournamentSlug={billingEnforced ? (onAirSlug ?? '') : slug}
                      stream={stream}
                      events={events ?? []}
                      pick={state.match?.onStream?.setId ?? ''}
                      onPick={(setId) => {
                        flushImmediateRef.current = true;
                        setState((prev) => setValueAtPath(prev, 'match.onStream', { setId }));
                      }}
                      showPicker={showPicker}
                      characters={state.match?.characters}
                      onCharacters={(next) => {
                        flushImmediateRef.current = true;
                        setState((prev) => setValueAtPath(prev, 'match.characters', next));
                      }}
                      hudFit={state.match?.hudFit}
                      onHudFit={(next) => {
                        setState((prev) => setValueAtPath(prev, 'match.hudFit', next));
                      }}
                      canStart={canStart}
                      autoStart={state.match?.autoStart === true}
                      onAutoStart={(next) => {
                        flushImmediateRef.current = true;
                        setState((prev) => setValueAtPath(prev, 'match.autoStart', next));
                      }}
                    />
                  </div>
                );
              }
              const variables = overlay.variables ?? [];
              // Countdown-driven sections get an On Air / Ready chip so the
              // operator can see at a glance whether the screen is counting.
              const serverNow = Date.now() + serverOffsetRef.current;
              const countdownBadge = (path) => {
                const endsAt = getValueAtPath(state, path);
                return endsAt && endsAt > serverNow
                  ? <span className="overlay-live-badge">On Air</span>
                  : <span className="pp-ready">Ready</span>;
              };
              const badge = overlay.slug === 'starting-soon'
                ? countdownBadge('starting.countdown.endsAt')
                : overlay.slug === 'be-right-back'
                  ? countdownBadge('brb.countdown.endsAt')
                  : undefined;
              const preview = (
                <OverlayPreview slug={overlay.slug} packageSlug={pkg.slug} />
              );
              const fieldGrid = (
                <div className="row g-2 mb-0">
                  {variables.map((variable) => {
                    // The title's lines read better on full-width rows.
                    const colClass = variable.key === 'tournament.name' || variable.key === 'tournament.org'
                      ? 'col-12'
                      : colSpanForType(variable.type);
                    return (
                      <div className={`mb-2 ${colClass}`} key={variable.key}>
                        <DynamicField
                          variable={variable}
                          value={getValueAtPath(state, variable.key)}
                          onChange={(next, opts) => {
                            if (opts?.immediate) flushImmediateRef.current = true;
                            setState((prev) => setValueAtPath(prev, variable.key, next));
                          }}
                          serverOffset={serverOffsetRef.current}
                        />
                      </div>
                    );
                  })}
                </div>
              );
              // Winner Screen: collapsed by default — the auto winner-screen
              // director stamps these fields on match end, so the form is the
              // manual fallback (vMix rigs, DQ fix-ups, corrections) rather
              // than everyday input. The summary shows the current card at a
              // glance without expanding.
              if (overlay.slug === 'winner-screen') {
                const w = state.winner ?? {};
                const who = [w.prefix, w.name].filter(Boolean).join(' ');
                return (
                  <div className="overlay-masonry__item" key={overlay.id}>
                    <OverlayCard name={overlay.name}>
                      {preview}
                      <p className="text-muted small mb-2">
                        Auto-filled when a match ends while “Auto winner
                        screen” is armed (OBS Control tab).
                      </p>
                      <details className="card-fold">
                        <summary className="small">
                          {who ? (
                            <>
                              Current: <strong>{who}</strong>
                              {w.character ? ` · ${w.character}` : ''} — edit manually
                            </>
                          ) : (
                            'No winner set — edit manually'
                          )}
                        </summary>
                        <div className="mt-2">{fieldGrid}</div>
                      </details>
                    </OverlayCard>
                  </div>
                );
              }
              return (
                <div className="overlay-masonry__item" key={overlay.id}>
                  <OverlayCard name={overlay.name} badge={badge}>
                    {preview}
                    {fieldGrid}
                  </OverlayCard>
                </div>
              );
            })}
          </div>

          {liveOnlyOverlays.length > 0 && (
            <div className="row g-3 mb-4">
              {liveOnlyOverlays.map((overlay) => (
                <div className="col-md-6 col-lg-4" key={overlay.id}>
                  <div className="card card-fui overlay-card h-100">
                    <div className="card-header d-flex align-items-center justify-content-between gap-2">
                      <h6 className="mb-0">
                        <img src="/images/section-icon.png" className="pp-icon" alt="" aria-hidden="true" /> {overlay.name}
                      </h6>
                      <span className="overlay-live-badge">Live</span>
                    </div>
                    <div className="card-body">
                      <OverlayPreview slug={overlay.slug} packageSlug={pkg.slug} tall />
                      <p className="text-muted small mb-0">
                        {OVERLAY_BLURB[overlay.slug]
                          ?? 'Fed by your start.gg link and shared settings — no per-overlay config.'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {overlayTabItems.length === 0 && (
            <p className="text-muted text-center small mb-4">
              No overlays granted in this package yet.
            </p>
          )}
        </>
      )}

      {/* ── Commentators tab ── two nameplates flanking the tournament logo. */}
      {currentTab === 'commentators' && (
        <>
          <div className="row g-3 mb-2">
            {[
              { side: 'left', label: 'Left commentator' },
              { side: 'right', label: 'Right commentator' },
            ].map(({ side, label }) => {
              const c = state.commentators?.[side] ?? {};
              const setCommentator = (field, value) =>
                setState((prev) => setValueAtPath(prev, `commentators.${side}.${field}`, value));
              return (
                <div className="col-md-6" key={side}>
                  <div className="card card-fui h-100">
                    <div className="card-header">
                      <h6 className="mb-0">
                        <img src="/images/section-icon.png" className="pp-icon" alt="" aria-hidden="true" /> {label}
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <label className="form-label small mb-1">
                          Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          value={c.name ?? ''}
                          onChange={(e) => setCommentator('name', e.target.value)}
                          placeholder="e.g. ANAKIN"
                        />
                      </div>
                      <div className={hasCameras ? 'mb-0' : 'mb-3'}>
                        <label className="form-label small mb-1">Handle</label>
                        <div className="input-group">
                          <span className="input-group-text">@</span>
                          <input
                            className="form-control"
                            value={c.handle ?? ''}
                            onChange={(e) => setCommentator('handle', e.target.value)}
                            placeholder="username (optional)"
                          />
                        </div>
                      </div>
                      {!hasCameras && (
                        <div className="mb-0">
                          <label className="form-label small mb-1">Role</label>
                          <input
                            className="form-control"
                            value={c.role ?? ''}
                            onChange={(e) => setCommentator('role', e.target.value)}
                            placeholder="e.g. Play-by-Play (optional)"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {hasCameras ? (
            <>
              <div className="form-check mb-2">
                <input
                  id="commentator-shared-camera"
                  type="checkbox"
                  className="form-check-input"
                  checked={getValueAtPath(state, 'display.commentator.shared') === true}
                  onChange={(e) => setCommentatorLayout(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="commentator-shared-camera">
                  {boothCameras ? 'Camera only (in place of Up Next and the bracket)' : 'One camera for both commentators'}
                </label>
              </div>
              <div className="form-check mb-2">
                <input
                  id="commentator-show-sponsors"
                  type="checkbox"
                  className="form-check-input"
                  checked={getValueAtPath(state, 'display.commentator.sponsors') !== false}
                  onChange={(e) =>
                    setState((prev) => setValueAtPath(prev, 'display.commentator.sponsors', e.target.checked))}
                />
                <label className="form-check-label" htmlFor="commentator-show-sponsors">
                  Show Sponsors
                </label>
              </div>
              <p className="text-muted small mb-4">
                {boothCameras ? (
                  <>
                    A commentator with no <strong>Name</strong> is left out; the named ones&rsquo; nameplates
                    stack in the left window&rsquo;s corner. The two windows show the Up Next screen and the
                    bracket (the OBS export puts those two overlays under them) unless <strong>Camera only</strong> is
                    on, which gives the camera one larger window instead. A change plays as a slide about 3 s
                    later, on the overlay and, through OBS Control, on OBS&rsquo;s camera, Up Next and Bracket.
                  </>
                ) : (
                  <>
                    A commentator with no <strong>Name</strong> is left out; each named one gets a camera window
                    with their nameplate, or both share one window when <strong>One camera for both</strong> is on
                    or only one is named. OBS Control shows the matching cameras about 3 s after a change.
                  </>
                )}
              </p>
            </>
          ) : (
            <>
              <div className="form-check mb-2">
                <input
                  id="commentator-show-logo"
                  type="checkbox"
                  className="form-check-input"
                  checked={getValueAtPath(state, 'display.commentator.logo') !== false}
                  onChange={(e) =>
                    setState((prev) => setValueAtPath(prev, 'display.commentator.logo', e.target.checked))}
                />
                <label className="form-check-label" htmlFor="commentator-show-logo">
                  Show Tournament Logo
                </label>
              </div>
              <p className="text-muted small mb-4">
                A commentator with no <strong>Name</strong> is hidden on the overlay.
              </p>
            </>
          )}
        </>
      )}

      {/* ── Sponsors tab ── (admin-gated; the tab only appears when enabled). */}
      {currentTab === 'sponsors' && user?.sponsorsEnabled && (
        <>
          <p className="text-muted small mb-3">
            {SPONSOR_TITLE_PACKAGES.has(packageSlug)
              ? 'The groups take turns on the sponsor bar, left to right; a group with no logos is skipped. ' +
                'Name each group as it should read on screen, in English and Arabic. Leave both empty to keep its usual name.'
              : 'Main, Gold and Silver rotate on the sponsor bar; tiers with no sponsors are skipped.'}
          </p>
          <div className="row g-3 mb-4">
            {TIERS.map((t) => (
              <div className="col-lg-4" key={t.id}>
                <TierColumn
                  tier={t}
                  sponsors={state.sponsors[t.id] || []}
                  title={state.sponsorTitles?.[t.id]}
                  onTitle={SPONSOR_TITLE_PACKAGES.has(packageSlug) ? setSponsorTitle : undefined}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                  busy={busy}
                  loading={busyTier === t.id}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Links tab ── one chip per overlay's OBS Browser Source URL. */}
      {currentTab === 'links' && (
        <div className="mb-4">
          <div className="row g-4">
            {linksList.map(({ overlay, url }) => (
              <Fragment key={overlay.id}>
                <div className="col-md-6 col-lg-4">
                  <div className="production-url-chip">
                    <label className="production-url-label">{overlay.name} Overlay</label>
                    <CopyableUrl value={url} />
                  </div>
                </div>
                {/* The "?flip=1" twin — same grant/token, players mirrored —
                    rides directly beside its parent. Pairs with the hidden
                    second source the OBS/vMix exports ship. */}
                {overlay.slug === IN_MATCH_SLUG && (
                  <div className="col-md-6 col-lg-4">
                    <div className="production-url-chip">
                      <label className="production-url-label">
                        {overlay.name}
                        {IN_MATCH_SWAPPED_SUFFIX} Overlay
                      </label>
                      <CopyableUrl value={swappedOverlayUrl(url)} />
                    </div>
                  </div>
                )}
              </Fragment>
            ))}
            {linkCount === 0 && (
              <div className="col-12">
                <p className="text-muted small mb-0">
                  No overlay URLs yet — set your slug &amp; stream on the Connection tab.
                </p>
              </div>
            )}
          </div>
          <p className="text-muted small mb-0 mt-3">
            Paste each URL into OBS as a Browser Source (1920×1080). They auto-refresh —
            no need to re-add.
          </p>
        </div>
      )}

      {/* ── OBS Control tab ── live scene switching over the obs-websocket
          on the machine running OBS: this browser talks straight to
          ws://127.0.0.1, and nothing OBS-shaped is ever exposed publicly
          (docs/OBS_SCENE_SWITCHING_BRIEF.md). From another device the tiles
          cut through that tab (src/lib/obsRemote.js). */}
      {currentTab === 'obs' && user?.obsExportEnabled !== false && (
        <div className="mb-4">
          <ObsControlCard
            packageSlug={packageSlug}
            overlays={overlays}
            onSharedChange={setCommentatorLayout}
          />
        </div>
      )}

      <div className="d-flex justify-content-end align-items-center mb-4 flex-wrap gap-2">
        {!user?.designRequestsDisabled && (
          <Link
            to={`/access/${packageSlug}`}
            className="adm-act me-auto"
          >
            <Plus size={13} aria-hidden="true" />
            Request a Custom Overlay
          </Link>
        )}
        <button
          type="button"
          className="adm-act adm-act--pink"
          onClick={() => setResetOpen(true)}
          disabled={busy}
        >
          <RotateCcw size={13} aria-hidden="true" />
          Reset Setup
        </button>
      </div>

      <ConfirmModal
        open={resetOpen}
        title="Reset setup?"
        body={
          <>
            <p className="mb-2">
              Resets all your saved tournament settings and uploaded
              assets, and cancels any active countdowns.
            </p>
            <p className="text-danger small mb-0">This cannot be undone.</p>
          </>
        }
        confirmLabel="Reset"
        cancelLabel="Cancel"
        confirmKind="danger"
        onConfirm={confirmResetAll}
        onClose={() => setResetOpen(false)}
      />
    </div>
  );
}
